/**
 * Version corrigée de useFreemiumQuota.ts
 * Fix de l'incohérence des quotas (8/50 -> X/20)
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { logError, ErrorFactory } from "../lib/error-handling";
import { isE2ETestingEnvironment } from "../lib/e2e-detection";
import { logger } from "../lib/logger";
import {
  CONVERSATION_QUOTAS,
  STORAGE_QUOTAS,
  PLANS,
  determineUserPlan,
  hasPaidAccess,
  isTrialActive,
  TRIAL_AI_CONVERSATIONS,
  type PlanId,
} from "../constants/quotas";
import { useAuth } from "../contexts/AuthContext";

export interface QuotaLimits {
  conversations: number;
  polls: number;
  storageSize: number; // in MB
  aiMessages: number;
  analytics: number;
  totalCredits: number;
}

export interface QuotaUsage {
  conversations: number;
  polls: number;
  datePolls: number;
  formPolls: number;
  quizz: number;
  availabilityPolls: number;
  aiMessages: number;
  totalCredits: number;
  storageUsed: number; // in MB
}

interface QuotaSyncState {
  data: QuotaUsage | null;
  pendingSync: boolean;
  lastSyncedAt?: string;
  lastError?: string;
}

export interface QuotaStatus {
  conversations: {
    used: number;
    limit: number;
    percentage: number;
    isNearLimit: boolean;
    isAtLimit: boolean;
  };
  polls: {
    used: number;
    limit: number;
    percentage: number;
    isNearLimit: boolean;
    isAtLimit: boolean;
  };
  aiMessages: {
    used: number;
    limit: number;
    percentage: number;
    isNearLimit: boolean;
    isAtLimit: boolean;
  };
  storage: {
    used: number;
    limit: number;
    percentage: number;
    isNearLimit: boolean;
    isAtLimit: boolean;
  };
  groups: {
    isPremium: boolean;
    hasPaidPlan: boolean;
  };
}

export interface UseFreemiumQuotaReturn {
  status: QuotaStatus;
  limits: QuotaLimits;
  usage: QuotaUsage;
  isAuthenticated: boolean;
  canCreateConversation: () => boolean;
  canCreatePoll: () => boolean;
  canSendMessage: () => boolean;
  canUseAnalytics: () => boolean;
  getStoragePercentage: () => number;
  authModalTrigger:
    | "conversation_limit"
    | "poll_limit"
    | "feature_locked"
    | "storage_full";
  showAuthIncentive: boolean;
  closeAuthModal: () => void;
}

// === SYSTÈME CENTRALISÉ ===
// Utiliser les quotas du nouveau modèle 3 niveaux
const getCentralizedLimits = (
  isAuthenticated: boolean,
  hasSoloSubscription: boolean,
  trialActive: boolean,
): QuotaLimits => {
  const userPlan = determineUserPlan(isAuthenticated, hasSoloSubscription);
  const planFeatures = PLANS[userPlan].features;

  // Un essai pose plan_type='solo', donc determineUserPlan répond SOLO et le
  // plafond de conversations devenait 999999 — le bandeau du tableau de bord
  // annonçait « Conversations illimitées » à quelqu'un qui en a 15, et le mur
  // serait tombé sans le moindre avertissement. Le carve-out existait déjà dans
  // useQuota ; il manquait ICI, et c'est ce hook que lit le tableau de bord.
  // Trouvé le 18/09 en regardant l'écran, pas en relisant le code.
  const conversationsPlafond =
    typeof planFeatures.aiConversations === "number"
      ? planFeatures.aiConversations
      : 999999; // "unlimited" -> 999999

  return {
    conversations: trialActive ? TRIAL_AI_CONVERSATIONS : conversationsPlafond,
    polls: 999999, // Illimité pour tous les plans (la création de polls est toujours gratuite)
    storageSize: STORAGE_QUOTAS[userPlan],
    // Aucune limite de messages par conversation n'est appliquée (la seule
    // borne est MAX_MODIFICATIONS_PER_POLL, tenue par GeminiChatInterface).
    aiMessages: 999999,
    analytics: userPlan === "ANONYMOUS" ? 0 : userPlan === "FREE" ? 10 : 50, // Analytics par plan
    totalCredits: trialActive ? TRIAL_AI_CONVERSATIONS : conversationsPlafond,
  };
};

const isE2EMode = () =>
  typeof window !== "undefined" &&
  (isE2ETestingEnvironment() ||
    (window as Window & { __IS_E2E_TESTING__?: boolean }).__IS_E2E_TESTING__ ===
      true);

export const useFreemiumQuota = (): UseFreemiumQuotaReturn => {
  const [showAuthModal, setShowAuthModal] = useState(false);

  const [authModalTrigger, setAuthModalTrigger] = useState<
    "conversation_limit" | "poll_limit" | "feature_locked" | "storage_full"
  >("conversation_limit");
  const [quotaRefreshKey, setQuotaRefreshKey] = useState(0);
  const [quotaUsage, setQuotaUsage] = useState<QuotaUsage>({
    conversations: 0,
    polls: 0,
    datePolls: 0,
    formPolls: 0,
    quizz: 0,
    availabilityPolls: 0,
    aiMessages: 0,
    totalCredits: 0,
    storageUsed: 0,
  });
  const [quotaState, setQuotaState] = useState<QuotaSyncState>({
    data: null,
    pendingSync: false,
  });
  const quotaRef = useRef<QuotaUsage | null>(null);
  const lastSyncRef = useRef<number>(0);

  // Récupérer le vrai état d'authentification depuis le contexte
  // 🛡️ Source de vérité : profile.plan_type (fix du 24/03/2026)
  // Ne PAS utiliser user.app_metadata.plan ni user.user_metadata.plan_type
  const { user, profile } = useAuth();
  const isAuthenticated = !!user;
  const hasSoloSubscription = hasPaidAccess(profile);
  // === SYSTÈME CENTRALISÉ ===
  const trialActive = isTrialActive(profile);
  const limits = getCentralizedLimits(
    isAuthenticated,
    hasSoloSubscription,
    trialActive,
  );
  // Pas de guestLimits - on utilise le système 3 niveaux

  // Pas besoin de extractUsageFromQuota - on utilise directement les données
  const extractUsageFromQuota = useCallback(
    (quota: QuotaUsage): QuotaUsage => quota,
    [],
  );

  // Charger les données de quota depuis Supabase (source unique de vérité)
  // cf. Docs/ARCHITECTURE/2026-04-01-STORAGE-ARCHITECTURE-FINAL.md
  const loadQuotaData = useCallback(async () => {
    try {
      if (!isAuthenticated) {
        const [
          { pollService },
          { getConversationsByGuest },
          { getFingerprint },
          { getOrCreateGuestQuota },
        ] = await Promise.all([
          import("../services/PollService"),
          import("../lib/storage/ConversationStorageSupabase"),
          import("../lib/guestIdentityService"),
          import("../lib/guestQuotaService"),
        ]);

        const fingerprint = await getFingerprint();
        const [guestPolls, guestConvs, guestQuota] = await Promise.all([
          pollService.getPollsByGuest(fingerprint).catch(() => []),
          getConversationsByGuest(fingerprint).catch(() => []),
          // Source de vérité du compteur "conversations IA à vie" pour les
          // invités : guest_quotas.conversations_created (consommé par
          // useAutoSave.createConversation, cf. correctif du 10/09/2026).
          // La table `conversations` Supabase (guestConvs ci-dessus) reste
          // vide côté invité — ConversationStorage y est 100% local — donc
          // ne peut pas servir de compteur, seulement de fallback si la
          // lecture du quota échoue.
          getOrCreateGuestQuota().catch(() => null),
        ]);

        const datePolls = guestPolls.filter((p) => p.type === "date").length;
        const formPolls = guestPolls.filter((p) => p.type === "form").length;
        const quizz = guestPolls.filter((p) => p.type === "quizz").length;
        const availabilityPolls = guestPolls.filter(
          (p) => p.type === "availability",
        ).length;
        const totalPolls = guestPolls.length;

        const usage: QuotaUsage = {
          conversations: guestQuota?.conversationsCreated ?? guestConvs.length,
          polls: totalPolls,
          datePolls,
          formPolls,
          quizz,
          availabilityPolls,
          aiMessages: 0,
          totalCredits: totalPolls + guestConvs.length,
          storageUsed: 0,
        };
        setQuotaUsage(usage);
        setQuotaState({ data: null, pendingSync: false });
        quotaRef.current = null;
        return;
      }

      // Authenticated: Source de vérité = Supabase (tables polls + conversations)
      // cf. Docs/ARCHITECTURE/2026-04-01-STORAGE-ARCHITECTURE-FINAL.md
      if (!user?.id) {
        return;
      }

      const [
        { pollService },
        { getConversations: getSupabaseConversations },
        { supabaseSelectMaybeSingle },
      ] = await Promise.all([
        import("../services/PollService"),
        import("../lib/storage/ConversationStorageSupabase"),
        import("../lib/supabaseApi"),
      ]);

      // Source de verite pour le compteur "X/N conversations" : la table
      // user_ai_credits (incrementee par la RPC consume_ai_credit cote Edge).
      // Avant le 2026-05-18 on lisait userConversations.length, ce qui comptait
      // les conversations stockees (peut diverger des credits consommes en cas
      // de signup post-anonyme, de tests, de debug, etc.). Cf. issue #737.
      const [userPolls, userConversations, aiCredits] = await Promise.all([
        pollService.getPollsByUser(user.id).catch(() => []),
        getSupabaseConversations(user.id).catch(() => []),
        supabaseSelectMaybeSingle<{
          credits_used: number;
          credits_remaining: number;
        }>(
          "user_ai_credits",
          {
            user_id: `eq.${user.id}`,
            select: "credits_used,credits_remaining",
          },
          { requireAuth: true },
        ).catch(() => null),
      ]);

      const datePolls = userPolls.filter((p) => p.type === "date").length;
      const formPolls = userPolls.filter((p) => p.type === "form").length;
      const quizz = userPolls.filter((p) => p.type === "quizz").length;
      const availabilityPolls = userPolls.filter(
        (p) => p.type === "availability",
      ).length;
      const totalPolls = userPolls.length;
      // Le compteur affiche dans la sidebar represente les credits IA consommes
      // selon la DB (source de verite). Pas le nombre de conversations stockees.
      // Fallback sur userConversations.length si la ligne user_ai_credits n'existe
      // pas encore (premier appel IA pas encore fait → 0).
      const conversationCount =
        aiCredits?.credits_used ?? userConversations.length;

      const usage: QuotaUsage = {
        conversations: conversationCount,
        polls: totalPolls,
        datePolls,
        formPolls,
        quizz,
        availabilityPolls,
        aiMessages: 0,
        totalCredits: conversationCount, // Le "crédit" SOLO/FREE = conversations IA
        storageUsed: 0,
      };

      setQuotaUsage(usage);
    } catch (error) {
      // Gérer les erreurs de configuration Supabase de manière silencieuse
      if (
        error instanceof Error &&
        (error.message.includes("Supabase configuration missing") ||
          error.message.includes("VITE_SUPABASE") ||
          error.message.includes("Failed to fetch") ||
          error.message.includes("ERR_NAME_NOT_RESOLVED") ||
          error.message.includes("NetworkError") ||
          error.name === "TypeError")
      ) {
        logger.warn(
          "Network or Supabase unavailable, using local quota fallback",
          "quota",
        );
        const defaultUsage: QuotaUsage = {
          conversations: 0,
          polls: 0,
          datePolls: 0,
          formPolls: 0,
          quizz: 0,
          availabilityPolls: 0,
          aiMessages: 0,
          totalCredits: 0,
          storageUsed: 0,
        };
        setQuotaUsage(defaultUsage);
        setQuotaState({ data: null, pendingSync: false });
        quotaRef.current = null;
        return;
      }

      logError(
        ErrorFactory.critical("Failed to load quota data", "QuotaError"),
        {
          component: "useFreemiumQuota",
          metadata: { originalError: error },
        },
      );
    }
  }, [isAuthenticated, extractUsageFromQuota, user?.id]);

  // Charger les données au montage et quand l'utilisateur change
  useEffect(() => {
    loadQuotaData();
  }, [loadQuotaData, isAuthenticated]);

  // Calcul de l'usage (simulation simplifiée)
  const usage = quotaUsage;

  const showAuthIncentive = !isAuthenticated;

  return {
    status: {
      // === SYSTÈME CENTRALISÉ : Utiliser les quotas globaux pour tout le monde ===
      conversations: calculateStatus(usage.totalCredits, limits.conversations), // 20 pour tout le monde
      polls: calculateStatus(usage.polls, limits.polls), // 20 pour tout le monde
      aiMessages: calculateStatus(usage.aiMessages, limits.aiMessages), // 20
      storage: calculateStatus(usage.storageUsed, limits.storageSize),
      groups: {
        // Reflète le vrai plan payant (SOLO) depuis profile.plan_type — cf. L167.
        // Avant : hardcodé false → récurrence/cadence jamais déverrouillée même
        // pour un abonné SOLO (feedback intent-controls 2026-07-08).
        isPremium: hasSoloSubscription,
        hasPaidPlan: hasSoloSubscription,
      },
    },
    usage,
    isAuthenticated,
    canCreateConversation: () => usage.totalCredits < limits.conversations,
    canCreatePoll: () => usage.polls < limits.polls,
    canSendMessage: () => usage.aiMessages < limits.aiMessages,
    canUseAnalytics: () => usage.aiMessages < limits.analytics,
    getStoragePercentage: () =>
      limits.storageSize > 0
        ? (usage.storageUsed / limits.storageSize) * 100
        : 0,
    authModalTrigger,
    showAuthIncentive,
    closeAuthModal: () => setShowAuthModal(false),
    // === SYSTÈME CENTRALISÉ : Exposer les limites pour l'UI ===
    limits: {
      conversations: limits.conversations,
      polls: limits.polls,
      storageSize: limits.storageSize,
      aiMessages: limits.aiMessages,
      analytics: limits.analytics,
      totalCredits: limits.totalCredits, // 20 pour tout le monde
    },
    // === DEBUG: Exposer l'état pour débogage ===
    // debug: {
    //   usage,
    //   isAuthenticated,
    //   limits,
    // },
  };
};

function calculateStatus(used: number, limit: number) {
  const percentage = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;

  return {
    used,
    limit,
    percentage,
    isNearLimit: percentage >= 80,
    isAtLimit: percentage >= 100,
  };
}
